import React, { useState } from 'react';
import clsx from 'clsx';
import { TableData } from './types';
import { formatValue, parseValue } from './formatter';

type EditableTableProps = {
  data: TableData;
  editedCells: Map<string, string>;
  onCellChange: (rowIndex: number, colIndex: number, value: string) => void;
  tablePrefix: string;
  deferFormattingWhileEditing?: boolean;
};

export const EditableTable = ({
  data,
  editedCells,
  onCellChange,
  tablePrefix,
  deferFormattingWhileEditing = false,
}: EditableTableProps) => {
  const [focusedCell, setFocusedCell] = useState<string | null>(null);
  const isEdited = (rowIndex: number, colIndex: number) => {
    return editedCells.has(`${tablePrefix}-${rowIndex}-${colIndex}`);
  };

  const isLocked = (rowIndex: number, colIndex: number) => {
    return (
      data.config.lockedRows?.includes(rowIndex) ||
      data.config.columns[colIndex].locked
    );
  };

  const handleChange = (rowIndex: number, colIndex: number, value: string) => {
    const format = data.config.columns[colIndex].format;
    const parsedValue = parseValue(value, format);
    onCellChange(rowIndex, colIndex, parsedValue);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {data?.config?.columns.map((column, index) => (
              <th
                key={index}
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                {column.label}
                {column.locked && (
                  <span className="ml-1 text-gray-400">(Bloqueado)</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data?.rows?.map((row, rowIndex) => (
            <tr 
              key={rowIndex} 
              className={clsx(
                "hover:bg-gray-50",
                data.config.lockedRows?.includes(rowIndex) && "bg-gray-50"
              )}
            >
              {row.map((cell, colIndex) => {
                const format = data.config.columns[colIndex].format;
                const locked = isLocked(rowIndex, colIndex);
                const edited = isEdited(rowIndex, colIndex);
                const cellKey = `${tablePrefix}-${rowIndex}-${colIndex}`;
                const rawValue = edited ? editedCells.get(cellKey)! : cell;
                const displayValue = deferFormattingWhileEditing && focusedCell === cellKey
                  ? String(rawValue ?? '')
                  : formatValue(rawValue, format);

                return (
                  <td
                    key={`${rowIndex}-${colIndex}`}
                    className={clsx(
                      "px-6 py-4 whitespace-nowrap",
                      locked ? "bg-gray-50" : "bg-white",
                      format === 'money' && "text-right",
                      format === 'number' && "text-right"
                    )}
                  >
                    {locked ? (
                      <span className="text-sm text-gray-900">{displayValue}</span>
                    ) : (
                      <input
                        type="text"
                        value={displayValue}
                        onFocus={() => setFocusedCell(cellKey)}
                        onBlur={() => setFocusedCell((current) => current === cellKey ? null : current)}
                        onChange={(e) => handleChange(rowIndex, colIndex, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === ',' || e.key === 'Decimal' || e.keyCode === 188) {
                            e.preventDefault();
                          }
                        }}
                        className={clsx(
                          "w-full px-2 py-1 rounded border focus:ring-2 focus:ring-blue-500 transition-colors",
                          edited ? "bg-blue-50 border-blue-300" : "border-transparent hover:border-gray-300",
                          format === 'money' && "text-right",
                          format === 'number' && "text-right"
                        )}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};